<script>
/**
 * Camada de apresentação do tour de boas-vindas: véu escuro com um recorte
 * (spotlight) sobre um elemento REAL da tela e um balão explicativo ao lado.
 *
 * Este componente é burro de propósito. Ele não sabe quando o tour deve
 * aparecer, quais passos existem nem como persistir a conclusão — isso tudo
 * continua em `components/onboarding-modal.vue`. Aqui só entra o "como
 * desenhar" e o "como achar/medir o alvo".
 *
 * Decisões que valem comentário:
 *
 * 1) O overlay vive no <body>, não dentro da sidebar. O `side-nav.vue` marca o
 *    item ativo percorrendo o DOM na mão (getElementsByClassName +
 *    parentElement/closest) e qualquer nó novo dentro da árvore <ul>/<li>
 *    quebraria esse realce. Por isso o componente se move para o body no
 *    mounted() e se posiciona por getBoundingClientRect() do alvo. No alvo em
 *    si só existe um atributo `data-tour` — nenhum elemento, nenhuma classe,
 *    nenhum z-index alterado.
 *
 * 2) O recorte é feito com `box-shadow: 0 0 0 9999px` num elemento vazio
 *    posicionado sobre o alvo, e não com quatro painéis de véu. Motivos: o
 *    buraco pode ter cantos arredondados (com quatro painéis, não); é um nó só
 *    para animar entre passos; e não exige tocar no z-index/stacking do alvo,
 *    que na sidebar é delicado (simplebar + `.vertical-menu` têm contexto
 *    próprio). Como sombra não recebe evento de ponteiro, os cliques ao redor
 *    do buraco são barrados por um `.st-tour-blocker` transparente por baixo —
 *    que também impede o usuário de navegar para outra tela no meio do tour.
 *
 * 3) Se o alvo não aparecer em ~1,2s, o componente emite `target-missing` e
 *    quem manda pula o passo. O usuário nunca fica preso esperando um elemento
 *    que não existe naquele perfil.
 */

// Tempo máximo de espera pelo alvo antes de desistir do passo.
const TARGET_TIMEOUT = 1200;
const TARGET_POLL = 60;

// Abaixo disto a sidebar é uma gaveta (ver _vertical.scss:
// `media-breakpoint-down(md)` = até 991.98px).
const MOBILE_BREAKPOINT = 992;

// Abaixo disto o balão ocupa a largura da tela, acima/abaixo do alvo.
const COMPACT_BREAKPOINT = 768;

const HOLE_PADDING = 6;
const GAP = 14;
const MARGIN = 12;
const TIP_WIDTH = 340;
const ARROW_INSET = 18;

export default {
  name: "OnboardingTour",
  props: {
    // { key, title, text, target, needsSidebar, cta }
    step: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 0,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Retângulo do buraco, em coordenadas de viewport (position: fixed).
      rect: null,
      placement: "center",
      tipTop: 0,
      tipLeft: 0,
      arrowOffset: 0,
      positioned: false,
      viewportWidth: typeof window !== "undefined" ? window.innerWidth : 0,
      viewportHeight: typeof window !== "undefined" ? window.innerHeight : 0,
      // Incrementado a cada passo: ativações antigas que ainda estavam
      // esperando um alvo se descartam sozinhas ao comparar o token.
      token: 0,
      sidebarOpenedByTour: false,
      pollTimer: null,
      safetyTimer: null,
      frameId: null,
      previouslyFocused: null,
      // Pulso curto no balão quando o usuário clica no véu, para não parecer
      // que a tela travou.
      nudge: false,
      nudgeTimer: null,
    };
  },
  computed: {
    compact() {
      return this.viewportWidth < COMPACT_BREAKPOINT;
    },
    tipWidth() {
      if (!this.compact) return TIP_WIDTH;
      return Math.max(240, this.viewportWidth - MARGIN * 2);
    },
    spotStyle() {
      if (!this.rect) return null;
      return {
        top: this.rect.top + "px",
        left: this.rect.left + "px",
        width: this.rect.width + "px",
        height: this.rect.height + "px",
      };
    },
    tipStyle() {
      return {
        top: this.tipTop + "px",
        left: this.tipLeft + "px",
        width: this.tipWidth + "px",
        visibility: this.positioned ? "visible" : "hidden",
      };
    },
    arrowStyle() {
      if (this.placement === "right" || this.placement === "left") {
        return { top: this.arrowOffset + "px" };
      }
      return { left: this.arrowOffset + "px" };
    },
    progressPercent() {
      if (!this.total) return 0;
      return Math.round(((this.index + 1) / this.total) * 100);
    },
  },
  watch: {
    step() {
      this.activate();
    },
  },
  mounted() {
    this.previouslyFocused = document.activeElement;

    // "Teleporte" manual (o Vue 2 não tem <teleport>): o overlay precisa ser
    // filho do <body> para não herdar o stacking context do #layout-wrapper.
    // O Vue remove o nó pelo parentNode real no destroy, então mover aqui é
    // seguro e não exige remoção manual.
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }

    window.addEventListener("resize", this.schedule, { passive: true });
    // capture=true para capturar também o scroll interno do simplebar da
    // sidebar, que não borbulha até a window.
    window.addEventListener("scroll", this.schedule, { passive: true, capture: true });
    document.addEventListener("keydown", this.onKeydown, true);

    this.activate();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.schedule, { passive: true });
    window.removeEventListener("scroll", this.schedule, { passive: true, capture: true });
    document.removeEventListener("keydown", this.onKeydown, true);

    this.clearPending();
    this.restoreSidebar();

    // Devolve o foco para onde ele estava antes do tour (normalmente o item
    // "Rever tutorial" do menu do perfil, no caso do replay).
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
      try {
        this.previouslyFocused.focus();
      } catch (e) {
        // Elemento pode ter saído do DOM enquanto o tour rodava.
      }
    }
  },
  methods: {
    clearPending() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.safetyTimer) {
        clearTimeout(this.safetyTimer);
        this.safetyTimer = null;
      }
      if (this.frameId) {
        window.cancelAnimationFrame(this.frameId);
        this.frameId = null;
      }
      if (this.nudgeTimer) {
        clearTimeout(this.nudgeTimer);
        this.nudgeTimer = null;
      }
    },
    // Clique no véu: o tour não fecha (fechar sem querer perderia o progresso),
    // mas o balão pisca para dizer "a ação está aqui".
    onBlockerClick() {
      if (this.nudgeTimer) clearTimeout(this.nudgeTimer);

      this.nudge = true;
      this.nudgeTimer = setTimeout(() => {
        this.nudge = false;
        this.nudgeTimer = null;
      }, 400);
    },

    // -----------------------------------------------------------------------
    // Gaveta do menu (mobile)
    // -----------------------------------------------------------------------
    // A sidebar só existe visualmente com `body.sidebar-enable` abaixo de
    // 992px. Passos que apontam para o menu abrem a gaveta; ao sair da seção
    // do menu ela é fechada — mas só se tiver sido o tour a abri-la.
    isMobile() {
      return this.viewportWidth < MOBILE_BREAKPOINT;
    },
    syncSidebar(step) {
      if (!this.isMobile()) return;

      const needs = !!(step && step.needsSidebar);
      const isOpen = document.body.classList.contains("sidebar-enable");

      if (needs && !isOpen) {
        document.body.classList.add("sidebar-enable");
        this.sidebarOpenedByTour = true;
        return;
      }

      if (!needs && this.sidebarOpenedByTour) {
        document.body.classList.remove("sidebar-enable");
        this.sidebarOpenedByTour = false;
      }
    },
    restoreSidebar() {
      if (!this.sidebarOpenedByTour) return;
      document.body.classList.remove("sidebar-enable");
      this.sidebarOpenedByTour = false;
    },

    // -----------------------------------------------------------------------
    // Ciclo de um passo
    // -----------------------------------------------------------------------
    activate() {
      this.token += 1;
      const token = this.token;
      const step = this.step;

      this.clearPending();
      this.positioned = false;

      if (!step) return;

      this.syncSidebar(step);

      if (!step.target) {
        // Passo sem alvo (boas-vindas / encerramento): balão centralizado.
        this.rect = null;
        this.afterMeasure(token);
        return;
      }

      // Failsafe de interface. Se, por qualquer motivo, a medição não terminar
      // (alvo que some entre o encontrar e o medir, rAF engasgado numa aba em
      // segundo plano), mostramos o balão mesmo assim, centralizado. O usuário
      // precisa SEMPRE ter os botões na mão: no celular não existe tecla Esc.
      this.safetyTimer = setTimeout(() => {
        if (token !== this.token || this.positioned) return;
        this.rect = null;
        this.afterMeasure(token);
      }, TARGET_TIMEOUT + 800);

      this.waitForTarget(step.target, token)
        .then((el) => {
          if (token !== this.token) return;

          if (!el) {
            // Alvo não apareceu: quem manda decide (pula o passo). Nunca
            // travamos a tela esperando.
            this.$emit("target-missing", step.key);
            return;
          }

          this.scrollIntoView(el);

          this.frameId = window.requestAnimationFrame(() => {
            this.frameId = null;
            if (token !== this.token) return;

            this.rect = this.rectOf(el);
            this.afterMeasure(token);
          });
        })
        .catch(() => {
          // A promessa não rejeita hoje, mas se um dia rejeitar o tour não
          // pode parar de pé: tratamos como alvo ausente.
          if (token !== this.token) return;
          this.$emit("target-missing", step.key);
        });
    },
    afterMeasure(token) {
      this.$nextTick(() => {
        if (token !== this.token) return;
        this.position();
        this.positioned = true;
        this.focusTip();
      });
    },

    /**
     * Aceita um seletor ou uma lista deles e devolve o primeiro que estiver
     * REALMENTE visível. A lista existe para alvos responsivos: o passo de
     * apresentação do menu aponta para o botão ☰ no mobile e para a sidebar
     * inteira no desktop, e quem decide é qual dos dois está pintado.
     *
     * Os seletores nunca vêm do usuário: são montados em onboarding-modal.vue
     * a partir das chaves do catálogo `utils/menu-map.js`, validadas contra
     * /^[a-z0-9-]+$/ antes de virarem seletor.
     */
    findVisibleTarget(target) {
      const candidates = Array.isArray(target) ? target : [target];

      for (let i = 0; i < candidates.length; i += 1) {
        const el = document.querySelector(candidates[i]);
        if (!el) continue;

        const r = el.getBoundingClientRect();
        // Largura/altura zeradas = escondido (display:none da gaveta no
        // mobile, v-show desligado, item colapsado). Não serve de alvo.
        if (r.width > 0 && r.height > 0) return el;
      }

      return null;
    },
    waitForTarget(selector, token) {
      return new Promise((resolve) => {
        const deadline = Date.now() + TARGET_TIMEOUT;

        const attempt = () => {
          if (token !== this.token) return resolve(null);

          const el = this.findVisibleTarget(selector);
          if (el) return resolve(el);

          if (Date.now() >= deadline) return resolve(null);

          this.pollTimer = setTimeout(attempt, TARGET_POLL);
        };

        attempt();
      });
    },
    scrollIntoView(el) {
      if (typeof el.scrollIntoView !== "function") return;

      const r = el.getBoundingClientRect();
      const vh = this.viewportHeight;

      // Já está inteiro na tela: rolar só mexeria a página à toa.
      if (r.top >= 0 && r.bottom <= vh) return;
      // Maior que a tela (o próprio menu inteiro, por exemplo): rolar não
      // resolve e ainda desalinharia o que o usuário já estava vendo.
      if (r.height >= vh * 0.9) return;

      try {
        // Sem `behavior: smooth` de propósito: a medição acontece no frame
        // seguinte e uma rolagem animada devolveria coordenadas erradas.
        // `block: center` funciona também dentro do simplebar da sidebar.
        el.scrollIntoView({ block: "center", inline: "nearest" });
      } catch (e) {
        // Navegadores antigos não aceitam o objeto de opções.
        el.scrollIntoView(false);
      }
    },
    rectOf(el) {
      let node = el;
      let r = node.getBoundingClientRect();

      // Grupos do MetisMenu (Rede, APVS, Afiliados, Acessa, Financeiro,
      // Suporte): quando o grupo está expandido o <li> engloba os subitens, o
      // que é ótimo (o texto do passo cita justamente esses subitens). Se
      // ficar alto demais para caber na tela, recuamos para o link do grupo.
      // A regra vale só para <li> — alvos que SÃO grandes por natureza (o
      // menu inteiro, no passo de apresentação) continuam destacados por
      // completo.
      if (node.tagName === "LI" && r.height > this.viewportHeight * 0.6) {
        const anchor = node.querySelector("a");
        if (anchor) {
          node = anchor;
          r = node.getBoundingClientRect();
        }
      }

      return {
        top: r.top - HOLE_PADDING,
        left: r.left - HOLE_PADDING,
        width: r.width + HOLE_PADDING * 2,
        height: r.height + HOLE_PADDING * 2,
      };
    },

    // -----------------------------------------------------------------------
    // Posicionamento do balão
    // -----------------------------------------------------------------------
    position() {
      const tip = this.$refs.tip;
      if (!tip) return;

      const tw = tip.offsetWidth || this.tipWidth;
      const th = tip.offsetHeight || 0;
      const vw = this.viewportWidth;
      const vh = this.viewportHeight;

      if (!this.rect) {
        this.placement = "center";
        this.tipTop = Math.max(MARGIN, Math.round((vh - th) / 2));
        this.tipLeft = Math.max(MARGIN, Math.round((vw - tw) / 2));
        return;
      }

      const r = this.rect;
      const fitsBelow = r.top + r.height + GAP + th <= vh - MARGIN;

      if (this.compact) {
        // Tela estreita: nunca ao lado — o balão dividiria a largura com o
        // alvo e ficaria com 4 palavras por linha.
        this.placement = fitsBelow ? "bottom" : "top";
      } else if (r.left + r.width + GAP + tw <= vw - MARGIN) {
        this.placement = "right";
      } else if (r.left - GAP - tw >= MARGIN) {
        this.placement = "left";
      } else if (fitsBelow) {
        this.placement = "bottom";
      } else {
        this.placement = "top";
      }

      let top;
      let left;

      if (this.placement === "right" || this.placement === "left") {
        top = r.top + r.height / 2 - th / 2;
        left = this.placement === "right" ? r.left + r.width + GAP : r.left - GAP - tw;
      } else {
        top = this.placement === "bottom" ? r.top + r.height + GAP : r.top - GAP - th;
        left = r.left + r.width / 2 - tw / 2;
      }

      this.tipTop = Math.round(this.clamp(top, MARGIN, Math.max(MARGIN, vh - th - MARGIN)));
      this.tipLeft = Math.round(this.clamp(left, MARGIN, Math.max(MARGIN, vw - tw - MARGIN)));

      // A seta acompanha o centro do alvo mesmo depois do clamp, para o balão
      // continuar "apontando" para o item certo.
      if (this.placement === "right" || this.placement === "left") {
        this.arrowOffset = Math.round(
          this.clamp(r.top + r.height / 2 - this.tipTop, ARROW_INSET, Math.max(ARROW_INSET, th - ARROW_INSET))
        );
      } else {
        this.arrowOffset = Math.round(
          this.clamp(r.left + r.width / 2 - this.tipLeft, ARROW_INSET, Math.max(ARROW_INSET, tw - ARROW_INSET))
        );
      }
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    // Reposicionamento em resize/scroll. Coalescido em requestAnimationFrame
    // para não medir o layout várias vezes no mesmo frame.
    schedule() {
      if (this.frameId) return;

      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = null;
        this.remeasure();
      });
    },
    remeasure() {
      this.viewportWidth = window.innerWidth;
      this.viewportHeight = window.innerHeight;

      const step = this.step;

      if (step && step.target) {
        const el = this.findVisibleTarget(step.target);
        // Alvo sumiu no meio de um resize: mantemos a última posição em vez de
        // piscar o véu inteiro. O passo seguinte revalida.
        if (!el) return;

        this.rect = this.rectOf(el);
      }

      this.$nextTick(this.position);
    },

    // -----------------------------------------------------------------------
    // Teclado e foco
    // -----------------------------------------------------------------------
    focusableInTip() {
      const tip = this.$refs.tip;
      if (!tip) return [];

      const nodes = tip.querySelectorAll("button:not([disabled]), a[href]");
      return Array.prototype.slice.call(nodes);
    },
    focusTip() {
      // O foco vai para a ação principal ("Próximo"/"Concluir"), não para o
      // container: num tour de ~18 passos, focar o container obrigaria dois
      // Tab por passo para chegar até ela. O conteúdo continua sendo anunciado
      // pelo `aria-live` do corpo, então não se perde nada de leitura — ganha-
      // -se um Enter por passo.
      const primary = this.$refs.primary;
      if (primary && typeof primary.focus === "function") {
        primary.focus();
        return;
      }

      const tip = this.$refs.tip;
      if (tip) tip.focus();
    },
    onKeydown(event) {
      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault();
        event.stopPropagation();
        this.$emit("skip");
        return;
      }

      if (event.key !== "Tab") return;

      // Foco preso no balão: enquanto o tour está aberto, o resto da tela está
      // inerte (o blocker barra o mouse), então deixar o Tab escapar levaria o
      // teclado para controles que não respondem.
      const items = this.focusableInTip();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const tip = this.$refs.tip;
      const active = document.activeElement;
      const first = items[0];
      const last = items[items.length - 1];

      if (!tip.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<template>
  <div v-if="step" class="st-tour">
    <!-- Barra todo clique fora do balão: sem isso o usuário navegaria para
         outra tela no meio do tour (e a sombra do spotlight, por ser sombra,
         não intercepta ponteiro).
         `is-veiled` cobre os passos SEM alvo (boas-vindas / encerramento):
         como quem escurece a tela é a sombra do spotlight, sem ele o véu
         precisa pintar o fundo, senão a tela ficaria clara nesses passos. -->
    <div
      class="st-tour-blocker"
      :class="{ 'is-veiled': !rect }"
      aria-hidden="true"
      @click="onBlockerClick"
    ></div>

    <div v-if="rect" class="st-tour-spot" :style="spotStyle" aria-hidden="true"></div>

    <!-- Atalho de mouse equivalente ao botão "Próximo". Fica fora da ordem de
         foco (aria-hidden) porque duplica uma ação que já existe no balão. -->
    <div
      v-if="rect"
      class="st-tour-hole"
      :style="spotStyle"
      aria-hidden="true"
      @click="$emit('next')"
    ></div>

    <div
      ref="tip"
      class="st-tour-tip"
      :class="['is-' + placement, { 'is-compact': compact, 'is-nudged': nudge }]"
      :style="tipStyle"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="st-tour-title"
      aria-describedby="st-tour-text"
    >
      <span v-if="rect" class="st-tour-arrow" :style="arrowStyle" aria-hidden="true"></span>

      <div class="st-tour-head">
        <span class="st-tour-counter" aria-current="step">
          Passo {{ index + 1 }} de {{ total }}
        </span>
        <button type="button" class="st-tour-skip" @click="$emit('skip')">Pular</button>
      </div>

      <div
        class="st-tour-progress"
        role="progressbar"
        aria-label="Progresso do tour"
        :aria-valuenow="index + 1"
        aria-valuemin="1"
        :aria-valuemax="total"
      >
        <span class="st-tour-progress-bar" :style="{ width: progressPercent + '%' }"></span>
      </div>

      <div class="st-tour-body" aria-live="polite">
        <!-- O contador visível vive no cabeçalho, fora desta região. Sem esta
             cópia sr-only, quem usa leitor de tela ouviria o conteúdo de cada
             passo sem nunca saber onde está na sequência. -->
        <span class="sr-only">Passo {{ index + 1 }} de {{ total }}.</span>
        <h2 id="st-tour-title" class="st-tour-title">{{ step.title }}</h2>
        <p id="st-tour-text" class="st-tour-text">{{ step.text }}</p>
      </div>

      <div class="st-tour-actions">
        <button
          type="button"
          class="st-tour-btn st-tour-btn-ghost"
          :disabled="index === 0"
          @click="$emit('previous')"
        >
          Voltar
        </button>

        <button
          v-if="step.cta"
          type="button"
          class="st-tour-btn st-tour-btn-soft"
          @click="$emit('cta')"
        >
          {{ step.cta.label }}
        </button>

        <button
          ref="primary"
          type="button"
          class="st-tour-btn st-tour-btn-primary"
          @click="$emit('next')"
        >
          {{ isLast ? "Concluir" : "Próximo" }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Escala de z-index do app (ver design/custom/structure/_vertical.scss):
   véu do menu 1000, sidebar 1001, topbar 1002. O tour fica acima de todos —
   sem precisar mexer no z-index de nenhum deles. */
.st-tour-blocker {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1090;
}

.st-tour-blocker.is-veiled {
  background: rgba(16, 24, 40, 0.62);
}

.st-tour-spot {
  position: fixed;
  z-index: 1091;
  border-radius: 10px;
  pointer-events: none;
  /* Primeira sombra: anel claro em volta do alvo. Segunda: o próprio véu, um
     spread gigante a partir da borda do buraco. A ordem importa — a primeira
     listada é pintada por cima. */
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.92), 0 0 0 9999px rgba(16, 24, 40, 0.62);
  transition: top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease;
}

.st-tour-hole {
  position: fixed;
  z-index: 1092;
  cursor: pointer;
  border-radius: 10px;
}

/* Flex column + max-height: o balão nunca pode ficar mais alto que a tela.
   Como o `.st-tour-blocker` deixa o resto da página inerte, um rodapé de ações
   fora da viewport (celular deitado, zoom de 200%) tornaria o tour
   inavançável. Aqui só o CORPO rola — contador, progresso, "Pular" e os
   botões continuam sempre visíveis. */
.st-tour-tip {
  position: fixed;
  z-index: 1093;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  padding: 1rem 1.15rem 1.1rem;
  border-radius: var(--st-radius-lg, 12px);
  background: var(--st-surface, #fff);
  color: var(--st-text, #312f4f);
  box-shadow: var(--st-shadow-lg, 0 18px 48px rgba(16, 24, 40, 0.28));
  transition: top 0.18s ease, left 0.18s ease;
}

.st-tour-tip:focus {
  outline: none;
}

.st-tour-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--st-surface, #fff);
  transform: rotate(45deg);
}

.is-right > .st-tour-arrow {
  left: -6px;
  margin-top: -6px;
}

.is-left > .st-tour-arrow {
  right: -6px;
  margin-top: -6px;
}

.is-bottom > .st-tour-arrow {
  top: -6px;
  margin-left: -6px;
}

.is-top > .st-tour-arrow {
  bottom: -6px;
  margin-left: -6px;
}

.is-center > .st-tour-arrow {
  display: none;
}

.st-tour-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.st-tour-counter {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--st-text-muted, #666e80);
}

/* O "Pular" é a saída de emergência de um tour que pode ter ~18 passos: nunca
   pode ficar menor que 24x24 (WCAG 2.2 - 2.5.8). As margens negativas
   devolvem o alinhamento visual com a borda do balão. */
.st-tour-skip {
  min-height: 24px;
  padding: 4px 6px;
  margin: -4px -6px -4px 0;
  border: 0;
  background: none;
  font-size: 0.8rem;
  color: var(--st-text-muted, #666e80);
  text-decoration: underline;
  cursor: pointer;
}

.st-tour-skip:hover {
  color: var(--st-brand, #27416e);
}

.st-tour-progress {
  flex: 0 0 auto;
  height: 4px;
  margin: 0.6rem 0 0.85rem;
  border-radius: var(--st-radius-sm, 6px);
  background: var(--st-border-soft, #e9ecef);
  overflow: hidden;
}

.st-tour-progress-bar {
  display: block;
  height: 100%;
  border-radius: var(--st-radius-sm, 6px);
  background: var(--st-brand, #27416e);
  transition: width 0.2s ease;
}

/* Único bloco que rola quando o texto não cabe. `min-height: 0` é obrigatório:
   sem ele um filho flex não encolhe abaixo do próprio conteúdo e o overflow
   nunca acontece. */
.st-tour-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.st-tour-title {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--st-text, #312f4f);
}

.st-tour-text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--st-text-muted, #666e80);
}

.st-tour-actions {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.st-tour-btn {
  min-height: 38px;
  padding: 0.4rem 0.95rem;
  border-radius: var(--st-radius, 8px);
  border: 1px solid transparent;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
}

.st-tour-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.st-tour-btn-ghost {
  border-color: var(--st-border, #dee2e6);
  background: var(--st-surface, #fff);
  color: var(--st-text, #312f4f);
}

/* Terceiro nível de ênfase, entre o fantasma ("Voltar") e o sólido
   ("Concluir"): o CTA do Guia do App é opcional, mas não é neutro. */
.st-tour-btn-soft {
  background: var(--st-brand-soft, rgba(39, 65, 110, 0.09));
  color: var(--st-brand, #27416e);
  font-weight: 600;
}

.st-tour-btn-primary {
  background: var(--st-brand, #27416e);
  color: var(--st-text-inverse, #fff);
}

.st-tour-btn:focus-visible,
.st-tour-skip:focus-visible {
  outline: 2px solid var(--st-focus-ring, #27416e);
  outline-offset: 2px;
}

.is-compact .st-tour-btn {
  flex: 1 1 auto;
}

/* Alvos de toque de 44px em TODA a faixa em que a interface é de toque — a
   gaveta do menu vai até 991,98px, não até os 768px do layout compacto do
   balão. A media query de ponteiro grosso cobre o resto (tablets largos). */
@media (max-width: 991.98px), (pointer: coarse) {
  .st-tour-btn,
  .st-tour-skip {
    min-height: 44px;
  }

  .st-tour-skip {
    padding: 4px 8px;
    margin-right: -8px;
  }
}

/* Retorno visual para o clique no véu: sem isto a tela parece travada. */
.st-tour-tip.is-nudged {
  animation: st-tour-pulse 0.4s ease;
}

@keyframes st-tour-pulse {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.025);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .st-tour-spot,
  .st-tour-tip,
  .st-tour-progress-bar {
    transition: none;
  }

  .st-tour-tip.is-nudged {
    animation: none;
  }
}
</style>
